// types
import { Account } from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// hooks
import { useReimbursements } from '../../../hooks/useReimbursements';

// mui
import { Grid, Tooltip, Typography } from '@mui/material';

// cards
import { CardKeyValue, CardKeyValueProps } from '../CardKeyValue/CardKeyValue';

// icons
import { InfoOutlined as IconInfo } from '@mui/icons-material';

// utils
import { formatCurrency } from '../../../utils/currency';

// props
export interface CardReimbursedProps {
	account?: Account;
}

export const CardReimbursed = ({ account }: CardReimbursedProps) => {
	// hooks
	const {
		isLoadingReimbursements,
		isLoadingTransactionsApplied,
		reimbursements,
		totalApplied,
		totalReimbursed,
	} = useReimbursements({ account });
	const { t } = useTranslation();

	// vars
	let currency: string | undefined = undefined;

	if (account) {
		currency = account.currency;
	}

	const CardKeyValueProps: CardKeyValueProps = {
		isLoading: isLoadingReimbursements || isLoadingTransactionsApplied,
		label: {
			children: (
				<Grid alignItems="center" container spacing={1}>
					<Grid item>
						<Typography sx={{ opacity: 0.8 }}>{t('applied')}</Typography>
					</Grid>
					<Grid item>
						<Tooltip
							arrow={true}
							placement="bottom"
							title={t('xOfY', {
								x: formatCurrency({ currency, value: totalApplied }),
								y: formatCurrency({ currency, value: totalReimbursed }),
							})}>
							<IconInfo />
						</Tooltip>
					</Grid>
				</Grid>
			),
		},
		severity: 'default',
		value: {
			children: formatCurrency({
				currency,
				value: totalApplied,
			}),
		},
	};

	if (!CardKeyValueProps.isLoading) {
		CardKeyValueProps.ChipStatusProps = {
			status: 'PENDING',
		};

		// determine severity based on percentage of applied to balance
		if (account?.balances && reimbursements.length) {
			const balance = account.balances.reduce((total, balance) => {
				return total + balance.balance;
			}, 0);

			const percentageApplied = balance > 0 ? (totalApplied / balance) * 100 : 0;

			if (percentageApplied >= 75) {
				if (CardKeyValueProps.ChipStatusProps) {
					CardKeyValueProps.ChipStatusProps.status = 'HIGH';
				}

				CardKeyValueProps.severity = 'success';
			} else if (percentageApplied >= 50) {
				if (CardKeyValueProps.ChipStatusProps) {
					CardKeyValueProps.ChipStatusProps.status = 'MODERATE';
				}
				CardKeyValueProps.severity = 'info';
			} else if (percentageApplied >= 25) {
				if (CardKeyValueProps.ChipStatusProps) {
					CardKeyValueProps.ChipStatusProps.status = 'MINIMAL';
				}
				CardKeyValueProps.severity = 'warning';
			} else {
				if (CardKeyValueProps.ChipStatusProps) {
					CardKeyValueProps.ChipStatusProps.status = 'LOW';
				}
				CardKeyValueProps.severity = 'error';
			}
		}
	}

	return <CardKeyValue {...CardKeyValueProps} />;
};
